import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../lib/api';
import type { Draft, Page, Site, Version } from '../../lib/types';
import { Banner, Card, CopyButton } from '../../lib/ui';
import { Hilfe } from '../help/Help';
import { t, tn } from '../../lib/i18n';

/**
 * Getting from "website added" to "banner live", in the product.
 *
 * Every competitor publishes a per-regulation setup checklist — and leaves it in their
 * help centre, where it cannot know which steps you have already done. Here it reads the
 * same data the rest of the console reads, so it ticks itself off and stops appearing
 * once there is nothing left to do.
 *
 * It is also the answer to what a new site used to open on: an analytics dashboard
 * reading "0,0 %", which looks like a broken product rather than an unstarted one.
 */
interface Install {
  checked_at: string;
  loader_found: boolean;
  site_key_matches: boolean;
  banner_rendered: boolean;
  trackers_before_consent: string[];
  error: string | null;
}

export function SetupChecklist({ site, onGoTo }: {
  site: Site;
  onGoTo: (tab: 'banner' | 'blocking') => void;
}) {
  const draft = useQuery({
    queryKey: ['draft-check', site.id],
    queryFn: () => api<Draft>(`/v1/sites/${site.id}/config`),
  });
  const versions = useQuery({
    queryKey: ['versions', site.id],
    queryFn: () => api<Page<Version>>(`/v1/sites/${site.id}/config/versions`),
  });
  const qc = useQueryClient();
  const install = useQuery({
    queryKey: ['install', site.id],
    queryFn: () => api<Install | null>(`/v1/sites/${site.id}/install`),
  });
  const check = useMutation({
    mutationFn: () => api<Install>(`/v1/sites/${site.id}/install`, { method: 'POST' }),
    onSuccess: (r) => qc.setQueryData(['install', site.id], r),
  });

  const published = (versions.data?.data.length ?? 0) > 0;
  const hasText = Boolean(draft.data?.texts?.['de']?.title);
  const inst = install.data;
  const installed = inst?.loader_found === true && inst.site_key_matches;

  const steps: Step[] = [
    {
      done: true,
      title: t('Website angelegt'),
      detail: t('{domain} · Schlüssel {key}', { domain: site.primary_domain, key: site.site_key }),
    },
    {
      done: hasText,
      title: t('Banner-Text geprüft'),
      detail: hasText
        ? t('Es gibt einen Titel und einen Text. Beides steht später im Nachweis.')
        : t('Das Banner hat noch keinen Text.'),
      action: { label: t('Zum Banner'), run: () => onGoTo('banner') },
    },
    {
      done: published,
      title: t('Veröffentlicht'),
      detail: published
        ? t('Besucher bekommen diese Fassung ausgeliefert.')
        : t('Solange nichts veröffentlicht ist, arbeitet das SDK mit seinen eingebauten Notfall-Regeln.'),
      action: { label: t('Zum Banner'), run: () => onGoTo('banner') },
    },
    {
      done: installed,
      title: t('Snippet eingebaut'),
      check: true,
      detail: install.isPending
        ? t('Wird geprüft …')
        : !inst
          ? t('Noch nicht geprüft.')
          : inst.error
            ? t('Die Seite war nicht erreichbar: {error}', { error: inst.error })
            : !inst.loader_found
              ? t('Auf der Startseite wurde kein Loader gefunden.')
              : !inst.site_key_matches
                ? t('Es lädt ein Loader, aber mit einem anderen Site-Schlüssel.')
                : t('Der Loader wurde gefunden und das Banner ist erschienen.'),
    },
  ];

  const doneSteps = steps.filter((s) => s.done);
  const openSteps = steps.filter((s) => !s.done);
  const open = openSteps.length;
  // Nothing left to do: the checklist stops being a screen and becomes nothing at all.
  if (open === 0) return null;

  return (
    // Only what is left is spelled out. Rendering all four steps in full meant that on a
    // site with one thing missing, three quarters of the first screen was a list of things
    // already done — and the dashboard someone actually came for started below the fold.
    <Card
      title={tn('Einrichtung — noch {n} Schritt', 'Einrichtung — noch {n} Schritte', open)}
      actions={(
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[var(--color-muted)]">
          {doneSteps.map((s) => (
            <span key={s.title} className="flex items-center gap-1">
              <span aria-hidden="true" className="text-[var(--color-live)]">✓</span>
              {s.title}
            </span>
          ))}
          <Hilfe to="snippet" />
        </span>
      )}
    >
      <ol className="space-y-3">
        {openSteps.map((s) => (
          <li key={s.title} className="flex gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full
                         border border-[var(--color-line)] bg-[var(--color-paper)]"
            />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-3">
                <span className="text-[13px] font-medium">{s.title}</span>
                {s.action && (
                  <button className="text-xs text-[var(--color-brand)] hover:underline"
                    onClick={s.action.run}>
                    {s.action.label}
                  </button>
                )}
                {s.check && (
                  <button className="text-xs text-[var(--color-brand)] hover:underline disabled:opacity-40"
                    disabled={check.isPending}
                    onClick={() => check.mutate()}>
                    {check.isPending ? t('wird geprüft …') : t('Jetzt prüfen')}
                  </button>
                )}
              </div>
              <p className="mt-0.5 text-xs text-[var(--color-muted)]">{s.detail}</p>
            </div>
          </li>
        ))}
      </ol>

      {/* Present and still too late: the snippet can sit below a tag manager, in which
          case it is installed and enforcing nothing. That distinction is the whole
          difference between a compliant site and one that looks compliant. */}
      {inst && (inst.trackers_before_consent?.length ?? 0) > 0 && (
        <div className="mt-4">
          <Banner kind="error">
            <span>
              {t('Trotz Banner wurden vor der Einwilligung Anbieter geladen: {vendors}. Meist steht das Snippet zu weit unten im <head>.',
                { vendors: inst.trackers_before_consent!.join(', ') })}
            </span>
          </Banner>
        </div>
      )}

      {!installed && (
        <div className="mt-4 border-t border-[var(--color-line)] pt-4">
          <div className="mb-2 flex items-center justify-between gap-3">
            <span className="eyebrow">{t('Das Snippet')}</span>
            <CopyButton value={snippet(site.site_key)} />
          </div>
          <pre className="overflow-x-auto rounded-[3px] border border-[var(--color-line)] bg-[var(--color-paper)] p-3 text-xs leading-relaxed">
{snippet(site.site_key)}
          </pre>
          <p className="mt-2 text-xs text-[var(--color-muted)]">
            {t('So weit oben im <head> wie möglich. Alles, was davor steht, kann das SDK nicht mehr anhalten — und genau das wäre der Verstoß, den es verhindern soll.')}
          </p>
        </div>
      )}
    </Card>
  );
}

interface Step {
  done: boolean;
  title: string;
  detail: string;
  check?: boolean;
  action?: { label: string; run: () => void };
}

/**
 * The tag the customer pastes.
 *
 * `allow` names the hosts that must survive the seed phase — the window between the
 * loader executing and the config arriving, in which unknown third parties are blocked
 * fail-closed. An allow rule in the published config cannot cover that window: it is
 * inside the thing that has not arrived yet. So the exceptions travel on the tag.
 */
export const snippet = (siteKey: string, allow: string[] = []): string =>
  `<script src="${import.meta.env['VITE_CDN_BASE'] ?? 'http://127.0.0.1:58080'}/v1/loader.js"\n        data-site="${siteKey}"`
  + (allow.length ? `\n        data-cmp-allow="${allow.join(',')}"` : '')
  + `></script>`;
